// Importing navigate from reach/router to solve issue with gatsby navigate not working correctly for blogs
import { navigate, useLocation } from "@reach/router";
import { NavDropDownCloseIcon, SearchIcon } from "@theme/assets";
import { useIsomorphicLayoutEffect } from "framer-motion";
import { useAtom } from "jotai";
import { type FC, type MouseEvent, forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  Box,
  Button,
  Flex,
  IconButton,
  NavMenuDrawer,
  NavSearchCarousel,
  SearchField,
  type ThemeUIStyleObject,
  ThemeWrapper,
  activeNavDropdownAtom,
} from "@theme/components";

import { useRootI18next } from "@theme/utils";

import type { StrapiDYCarouselModuleProps } from "@ms/gatsby-site/src/types/strapi";
import type { MsTheme } from "@theme/themes";

interface NavSearchButtonProps {
  searchModalCarousel?: Pick<StrapiDYCarouselModuleProps, "dyRecSelector">;
  sx?: ThemeUIStyleObject;
  theme?: MsTheme;
  includeShim?: boolean;
}

const SEARCH_INDEX = 99;
const SEARCH_URL = "/pages/search";

const KeyboardShim = forwardRef<HTMLInputElement>(function KeyboardShim(props, ref) {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    setContainer(document.body);
  }, []);

  if (!container) return null;

  return createPortal(
    <input
      ref={ref}
      id="keyboard-shim"
      style={{ position: "fixed", top: 0, left: 0, width: 1, height: 1, opacity: 0 }}
      inputMode="text"
      autoComplete="off"
      aria-label="Keyboard opener"
      tabIndex={-1}
      {...props}
    />,
    container,
  );
});

// Note: This component is shadowed. All updates must be done in both gatsby-theme and gatsby-site
// Shadowed for the search autocomplete suggestions
export const NavSearchButton: FC<NavSearchButtonProps> = ({
  searchModalCarousel,
  theme = "LIGHT",
  includeShim,
  ...props
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const keyboardShimRef = useRef<HTMLInputElement>(null);

  const { language, routed } = useRootI18next();
  const rootUrl = routed ? `/${language}${SEARCH_URL}` : `${SEARCH_URL}`;
  const { pathname } = useLocation();

  const [activeMenu, setActiveMenu] = useAtom(activeNavDropdownAtom);
  const [value, setValue] = useState("");

  const isActive = activeMenu === SEARCH_INDEX;

  const toggleMenu = () => {
    setActiveMenu(isActive ? -1 : SEARCH_INDEX);
  };

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    toggleMenu();

    keyboardShimRef.current?.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      const input = searchFieldRef.current;
      if (!input) return;

      const end = input.value?.length ?? 0;
      input.focus();
      input.setSelectionRange(end, end);
    });
  };

  useEffect(() => {
    if (!isActive) setValue("");
  }, [activeMenu]);

  useEffect(() => {
    if (isActive) toggleMenu();
  }, [pathname]);

  return (
    <Box {...props}>
      <ThemeWrapper theme={theme}>
        <Button
          variant="icon"
          aria-label="Search"
          role="menuitem"
          ref={buttonRef}
          onClick={(e) => handleClick(e)}
          sx={{
            "&&:active": { bg: "transparent" },
            "@media (hover: none) and (pointer: coarse)": {
              "&&:hover": { bg: "transparent" },
            },
          }}
        >
          <SearchIcon />
        </Button>
      </ThemeWrapper>
      {/* For mobile virtual keyboard accessibility */}
      {includeShim && <KeyboardShim ref={keyboardShimRef} />}
      {isActive && (
        <NavMenuDrawer navCategoryTitle="Search" buttonRef={buttonRef}>
          <Box sx={{ py: ["s400", null, null, "s1000"], px: "s500", width: "100%" }}>
            <Flex
              sx={{
                flexDirection: "column",
                gap: "s600",
                px: [null, null, null, "s800"],
                marginX: "auto",
                maxWidth: "1216px",
              }}
            >
              <Flex sx={{ gap: "s400", alignItems: "start" }}>
                <SearchField
                  ref={searchFieldRef}
                  value={value}
                  onValueChange={setValue}
                  onKeyDown={() => {
                    toggleMenu();
                    navigate(`${rootUrl}?q=${value}`);
                  }}
                />
                <IconButton
                  aria-label="Close Search Popup"
                  onClick={toggleMenu}
                  sx={{
                    display: ["block", null, null, "none"],
                    width: "fit-content",
                    p: 0,
                    marginTop: "s100",
                    paddingTop: "s50",
                    ":hover": {
                      bg: "transparent",
                    },
                  }}
                >
                  <NavDropDownCloseIcon sx={{ color: "goldPrimary" }} />
                </IconButton>
              </Flex>
              <NavSearchCarousel {...searchModalCarousel} />
            </Flex>
          </Box>
        </NavMenuDrawer>
      )}
    </Box>
  );
};
